import './lib/audiencePreferences'
import './styles.css'
import { applicationMountElement } from './lib/applicationMount'
import { readPublicRoute } from './lib/publicRoute'
import { audienceConfirmationWasCaptured, audiencePreferencesSuppressClarity } from './lib/audiencePreferences'
import {
  captureInitialCampaign,
  enableAggregateAnalytics,
  enableAnalyticsProviders,
  setAnalyticsContext,
  safeCampaignSearch,
  trackEvent,
  trackPageView,
} from './lib/analytics'
import { readAnalyticsConsent } from './lib/analyticsConsent'
import { resolveBrowserLocale } from './i18n'
import { documentAnalyticsStartup } from './lib/documentAnalytics'
import { isLandingExperienceRoute } from './landing/gate'

function initializeDocumentAnalytics(
  route: ReturnType<typeof readPublicRoute>,
  pageType: 'atlas' | 'municipality_profile' | 'municipality_indicator' | 'content',
  trackPageViewImmediately: boolean,
) {
  enableAggregateAnalytics()
  setAnalyticsContext({
    locale: route?.locale,
    pageType,
    routeKind: route?.pageType === 'embed' ? 'atlas' : (route?.pageType ?? 'semantic'),
    metricId: route?.selection.metricId,
    measureId: route?.selection.measureId,
    geographyLevel: route?.selection.level,
    period: route?.selection.period,
    municipalityCode: route?.selectedCode,
    hasSelectedArea: Boolean(route?.selectedCode),
  })
  // Atlas pageviews wait for the reconciled default selection so GA receives
  // useful coarse dimensions without treating every query-state change as a
  // new page. Static content pages can be measured immediately.
  if (trackPageViewImmediately) trackPageView()
  if (readAnalyticsConsent()?.decision === 'granted') {
    enableAnalyticsProviders({ suppressClarity: audiencePreferencesSuppressClarity() })
    if (audienceConfirmationWasCaptured()) trackEvent('subscription_confirmation')
  }
}

// Capture the small allowlisted campaign taxonomy before atlas reconciliation
// rewrites the query string. Audience bearer/subscriber state was synchronously
// scrubbed by the first import above and is never part of this snapshot.
captureInitialCampaign(window.location.search)
const initialRoute = readPublicRoute()
const startup = window.location.pathname === '/'
  ? { action: 'redirect' as const, redirect: `/${resolveBrowserLocale(window.location.search)}/${safeCampaignSearch(window.location.search)}` }
  : documentAnalyticsStartup(
      initialRoute,
      window.location,
      Boolean(applicationMountElement(document)),
    )
if (startup.action === 'redirect') {
  // Do not record a stale semantic path when legacy arbitrary atlas state is
  // being moved to its neutral atlas URL.
  window.location.replace(startup.redirect)
} else {
  initializeDocumentAnalytics(initialRoute, startup.pageType, startup.trackPageViewImmediately)
}

async function startLandingExperience() {
  if (!isLandingExperienceRoute(initialRoute)) return
  const landingPromise = import('./landing')
  const consentPromise = Promise.all([
    import('./components/ConsentManager'),
    import('react'),
    import('react-dom/client'),
  ])
  const { initializeLandingExperience } = await landingPromise
  initializeLandingExperience()
  const [{ ConsentManager }, { StrictMode, createElement }, { createRoot }] = await consentPromise
  const consentRoot = document.querySelector<HTMLElement>('[data-landing-consent]')
  if (consentRoot && initialRoute?.locale) {
    createRoot(consentRoot).render(createElement(StrictMode, null, createElement(ConsentManager, { locale: initialRoute.locale })))
  }
}

async function startAtlas() {
  const rootElement = applicationMountElement(document)
  // Municipality profiles are build-time content pages. Their explicit
  // Explore link opens a neutral atlas route, so the profile remains visible
  // and the large atlas/MapLibre chunks are not requested on content pages.
  if (!rootElement) return

  const [
    { default: App },
    { StrictMode, createElement },
    { createRoot },
  ] = await Promise.all([
    import('./App'),
    import('react'),
    import('react-dom/client'),
  ])

  createRoot(rootElement).render(createElement(StrictMode, null, createElement(App)))
}

function scheduleAtlasStart() {
  const rootElement = applicationMountElement(document)
  if (!rootElement) return
  if (initialRoute?.pageType === 'embed') {
    void startAtlas()
    return
  }
  const boot = rootElement.querySelector('[data-atlas-boot]')
  if (!boot) {
    void startAtlas()
    return
  }
  let started = false
  let timer = 0
  const start = () => {
    if (started) return
    started = true
    window.clearTimeout(timer)
    window.removeEventListener('pointerdown', start)
    window.removeEventListener('keydown', start)
    void startAtlas()
  }
  window.addEventListener('pointerdown', start, { passive: true })
  window.addEventListener('keydown', start)
  // Show the stable first frame before paying the WebGL startup cost. Any user
  // intent starts the atlas immediately; otherwise it transitions on its own.
  timer = window.setTimeout(start, 2_500)
}

if (startup.action === 'initialize') {
  // Campaign capture, redirect reconciliation and consent-aware analytics are
  // intentionally established before the optional landing enhancement loads.
  // Every other semantic page remains a zero-runtime static document.
  void startLandingExperience()
  scheduleAtlasStart()
}
